import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Form,
  Modal,
  Row,
  Spinner} from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import { Flag } from 'react-bootstrap-icons'
import {
  BsArrowClockwise,
  BsEyeFill,
  BsFunnelFill,
  BsPencilFill,
  BsSearch,
  BsThreeDotsVertical,
  BsTrashFill} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import axios from 'axios'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'

import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import TablePagination from './commonComponents/TablePagination'
import { useInstitute } from './commonComponents/InstituteContext'
import { useNavigate } from 'react-router-dom'
import { BASE_URL } from './commonComponents/ApiEndpoints'
import {
  deleteAnnouncementRequest,
  fetchAnnouncementsList,
  getAnnouncementErrorMessage,
  parseAnnouncementListResponse} from './commonComponents/announcementApi'

const INITIAL_ANNOUNCEMENT_FILTERS = {
  class_id: '',
  course_id: '',
  scope: '',
  status: ''}

const truncate = (text, len = 80) => {
  if (!text) return '—'
  const s = String(text)
  return s.length <= len ? s : `${s.slice(0, len)}…`
}

const formatDateTime = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'})
}

const statusBadge = (s) => {
  const v = String(s || '').toLowerCase()
  if (v === 'published') return <Badge bg="success">Published</Badge>
  if (v === 'draft') return <Badge bg="secondary">Draft</Badge>
  if (v === 'archived') return <Badge bg="dark">Archived</Badge>
  return <Badge bg="light" text="dark">{s || '—'}</Badge>
}

const priorityBadge = (p) => {
  const v = String(p || '').toLowerCase()
  if (v === 'high') return <Badge bg="danger">High</Badge>
  if (v === 'mid') return <Badge bg="warning" text="dark">Mid</Badge>
  if (v === 'low') return <Badge bg="secondary">Low</Badge>
  return <Badge bg="light" text="dark">—</Badge>
}

export default function CreateAnnouncement() {
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo } = useInstitute()

  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [classesList, setClassesList] = useState([])
  const [coursesList, setCoursesList] = useState([])

  const [filters, setFilters] = useState(INITIAL_ANNOUNCEMENT_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_ANNOUNCEMENT_FILTERS)

  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const [showFilterCard, setShowFilterCard] = useState(true)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [currentPage, setCurrentPage] = useState(1)
  const [classSearch, setClassSearch] = useState('')
  const [courseSearch, setCourseSearch] = useState('')

  const buildParams = (f) => {
    const p = {}
    if (f.class_id) p.class_id = Number(f.class_id)
    if (f.course_id) p.course_id = Number(f.course_id)
    if (f.scope) p.scope = f.scope
    if (f.status) p.status = f.status
    return p
  }

  const loadList = useCallback(
    async (snapshot) => {
      setLoading(true)
      setCurrentPage(1)
      try {
        const res = await fetchAnnouncementsList(buildParams(snapshot))
        setRows(parseAnnouncementListResponse(res))
      } catch (err) {
        const msg = getAnnouncementErrorMessage(err, navigate)
        toast.error(msg)
        setRows([])
      } finally {
        setLoading(false)
      }
    },
    [navigate],
  )

  useEffect(() => {
    setAppliedFilters(INITIAL_ANNOUNCEMENT_FILTERS)
    loadList(INITIAL_ANNOUNCEMENT_FILTERS)
  }, [loadList])

  useEffect(() => {
    const loadClassAndCourse = async () => {
      try {
        const institueId = Cookies.get('institute_id')
        const headers = {
          'institute-Id': Number(institueId),
          Authorization: `Bearer ${user?.token}`}
        const [classRes, courseRes] = await Promise.all([
          axios.get(`${BASE_URL}/class/all`, { headers }),
          axios.get(`${BASE_URL}/course/all`, { headers }),
        ])
        setClassesList(classRes?.data?.data || [])
        setCoursesList(courseRes?.data?.data || [])
      } catch {
        setClassesList([])
        setCoursesList([])
      }
    }
    loadClassAndCourse()
  }, [user?.token])

  const getClassId = (c) => String(c?.id ?? c?.class_id ?? '')
  const getClassLabel = (c) => c?.class_name || c?.name || `Class ${getClassId(c)}`
  const getCourseId = (c) => String(c?.id ?? c?.course_id ?? '')
  const getCourseLabel = (c) =>
    c?.course_name || c?.name || c?.title || `Course ${getCourseId(c)}`

  const handleClassSearchChange = (value) => {
    setClassSearch(value)
    const selectedClass = classesList.find(
      (c) => getClassLabel(c).toLowerCase() === String(value || '').toLowerCase(),
    )
    setFilters((prev) => ({
      ...prev,
      class_id: selectedClass ? getClassId(selectedClass) : ''}))
  }

  const handleCourseSearchChange = (value) => {
    setCourseSearch(value)
    const selectedCourse = coursesList.find(
      (c) => getCourseLabel(c).toLowerCase() === String(value || '').toLowerCase(),
    )
    setFilters((prev) => ({
      ...prev,
      course_id: selectedCourse ? getCourseId(selectedCourse) : ''}))
  }

  useEffect(() => {
    if (!filters.class_id) {
      setClassSearch('')
      return
    }
    const selectedClass = classesList.find((c) => getClassId(c) === String(filters.class_id))
    setClassSearch(selectedClass ? getClassLabel(selectedClass) : '')
  }, [filters.class_id, classesList])

  useEffect(() => {
    if (!filters.course_id) {
      setCourseSearch('')
      return
    }
    const selectedCourse = coursesList.find((c) => getCourseId(c) === String(filters.course_id))
    setCourseSearch(selectedCourse ? getCourseLabel(selectedCourse) : '')
  }, [filters.course_id, coursesList])

  const totalPages = Math.ceil(rows.length / itemsPerPage)
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage
  const indexOfLastItem = currentPage * itemsPerPage
  const displayRows = useMemo(
    () => rows.slice(indexOfFirstItem, indexOfLastItem),
    [rows, indexOfFirstItem, indexOfLastItem],
  )

  useEffect(() => {
    if (rows.length === 0) return
    const tp = Math.ceil(rows.length / itemsPerPage) || 1
    if (currentPage > tp) setCurrentPage(tp)
  }, [rows.length, itemsPerPage, currentPage])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters((prev) => ({
      ...prev,
      [name]: value}))
  }

  const applyFilters = () => {
    setAppliedFilters({ ...filters })
    loadList(filters)
  }

  const resetFilters = () => {
    setClassSearch('')
    setCourseSearch('')
    setFilters(INITIAL_ANNOUNCEMENT_FILTERS)
    setAppliedFilters(INITIAL_ANNOUNCEMENT_FILTERS)
    loadList(INITIAL_ANNOUNCEMENT_FILTERS)
  }

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return
    setDeleting(true)
    try {
      await deleteAnnouncementRequest(deleteTarget.id)
      toast.success('Announcement deleted.')
      setDeleteTarget(null)
      loadList(appliedFilters)
    } catch (err) {
      const msg = getAnnouncementErrorMessage(err, navigate)
      toast.error(msg)
    } finally {
      setDeleting(false)
    }
  }

  const rowId = (r) => r.id ?? r.announcement_id

  const handleRefreshList = () => {
    resetFilters()
  }

  return (
    <>
      <style>
        {`
          .page-title {
            font-family: 'Inter', system-ui, sans-serif;
            letter-spacing: -0.02em;
            color: #223843;
          }
          .modern-card {
            background: #ffffff;
            border: 1px solid #D1D3D4;
            border-radius: 12px;
            overflow: hidden;
          }
          .table td {
            overflow: visible !important;
            user-select: text;
          }
          .table tbody tr {
            overflow: visible !important;
            cursor: default;
          }
          .table-card {
            overflow: visible !important;
          }
          .table-wrapper {
            overflow-x: auto;
            overflow-y: visible !important;
            position: relative;
          }
          .table td, .table th { user-select: text; }
          .ann-filter-select {
            border-radius: 8px;
            border: 1px solid #D1D3D4;
            font-size: 14px;
            height: 40px;
            transition: all 0.2s ease;
            background-color: #ffffff;
            font-family: Inter, system-ui, sans-serif;
          }
          .dropdown-toggle-no-caret::after {
            display: none !important;
          }
          .dropdown-menu {
            z-index: 9999 !important;
            position: absolute !important;
          }
          .dropdown-menu.show {
            z-index: 9999 !important;
            position: absolute !important;
            transform: none !important;
          }
          .table {
            overflow: visible !important;
          }
          @media (max-width: 768px) {
            .ann-message-cell {
              max-width: none !important;
              white-space: normal !important;
              word-break: break-word !important;
              overflow-wrap: anywhere !important;
              line-height: 1.45 !important;
            }
          }
        `}
      </style>

      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: '12px',
          paddingRight: '12px'}}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px'}}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
                gap: '12px'}}
            >
              <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0}}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3
                    className="m-0 fw-bold page-title text-start"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: isMobile ? '14px' : 'clamp(18px, 2.5vw, 22px)',
                      lineHeight: '1.2'}}
                  >
                    Announcements
                  </h3>
                  <p className="m-0 mt-0 text-start" style={{ fontSize: isMobile ? '10px' : '13px', color: '#6b7280' }}>
                    View and manage announcements.
                  </p>
                </div>
              </div>

              {!isMobile && (
                <div className="d-flex align-items-center gap-3" style={{ justifyContent: 'flex-end' }}>
                  <ProfileDropdown logout={logout} notificationCount={3} showNotifications={true} />
                </div>
              )}
            </div>
          </div>
        </div>

        <Container
          fluid
          className="p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px'}}
        >
          {/* Filter toggle + page size (match AddStudent.js) */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  gap: '8px',
                  border: '1px solid #D1D3D4'}}
              >
                <span
                  style={{
                    color: '#6b7280',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                >
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value, 10))
                    setCurrentPage(1)
                  }}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '4px 32px 4px 12px',
                    minWidth: '140px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    height: '36px',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#223843'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1D3D4'
                  }}
                >
                  <option value="100">100 per page</option>
                  <option value="200">200 per page</option>
                  <option value="300">300 per page</option>
                </Form.Select>
              </div>
              <button
                type="button"
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>

            <button
              type="button"
              className="d-none d-md-flex align-items-center justify-content-center"
              onClick={() => navigate('/announcement/create')}
              style={{
                borderRadius: '10px',
                padding: '6px 10px',
                height: '44px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'}}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1f323a'
                e.currentTarget.style.transform = 'scale(1.03)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#223843'
                e.currentTarget.style.transform = 'scale(1)'
              }}
              title="Add New"
            >
              <Flag size={18} />
              <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Add New Announcement
              </span>
            </button>
          </div>

          <div className="d-md-none d-flex justify-content-end mb-4">
            <button
              type="button"
              onClick={() => navigate('/announcement/create')}
              style={{
                borderRadius: '10px',
                padding: '6px 10px',
                height: '44px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'}}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1f323a'
                e.currentTarget.style.transform = 'scale(1.03)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#223843'
                e.currentTarget.style.transform = 'scale(1)'
              }}
              title="Add New"
            >
              <Flag size={18} />
              <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Add New Announcement
              </span>
            </button>
          </div>

          {/* Collapsible filter card (match AddStudent.js) */}
          <div
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0'}}
          >
            {showFilterCard && (
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden'}}
              >
                <div
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6'}}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'}}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div className="text-start">
                        <h5
                          className="m-0 fw-semibold"
                          style={{
                            color: '#111827',
                            fontSize: '16px',
                            fontFamily: 'Inter, system-ui, sans-serif'}}
                        >
                          Search &amp; Filter Announcements
                        </h5>
                        <p className="m-0 mt-1" style={{ fontSize: '13px', color: '#6b7280' }}>
                          Find announcements quickly with advanced filters
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRefreshList}
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        backgroundColor: '#223843',
                        border: 'none',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'}}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#223843'
                        e.currentTarget.style.transform = 'scale(1.05)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#223843'
                        e.currentTarget.style.transform = 'scale(1)'
                      }}
                      title="Refresh"
                    >
                      <BsArrowClockwise size={20} />
                    </button>
                  </div>
                </div>

                <Card.Body className="p-4 text-start" style={{ backgroundColor: '#ffffff' }}>
                  <Row className="g-4">
                    <Col xs={12}>
                      <div
                        style={{
                          padding: '20px',
                          backgroundColor: '#F3F4F6',
                          borderRadius: '8px',
                          border: '1px solid #D1D3D4'}}
                      >
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#223843',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'}}
                          >
                            <BsFunnelFill style={{ color: '#ffffff', fontSize: '16px' }} />
                          </div>
                          <label
                            className="fw-semibold mb-0"
                            style={{
                              color: '#111827',
                              fontSize: '14px',
                              fontFamily: 'Inter, system-ui, sans-serif'}}
                          >
                            Filter by Class, Course, Scope &amp; Status
                          </label>
                        </div>
                        <Row className="g-3">
                          <Col xs={12} md={3}>
                            <label
                              className="fw-semibold mb-2 d-block"
                              style={{
                                color: '#6b7280',
                                fontSize: '13px',
                                fontFamily: 'Inter, system-ui, sans-serif'}}
                            >
                              Class
                            </label>
                            <Form.Control
                              type="text"
                              list="announcement-class-options"
                              value={classSearch}
                              onChange={(e) => handleClassSearchChange(e.target.value)}
                              placeholder="Search class"
                              className="ann-filter-select"
                              onFocus={(e) => {
                                e.target.style.borderColor = '#223843'
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#D1D3D4'
                              }}
                            />
                            <datalist id="announcement-class-options">
                              {classesList.map((c) => {
                                const cid = getClassId(c)
                                return (
                                  <option key={cid} value={getClassLabel(c)} />
                                )
                              })}
                            </datalist>
                          </Col>
                          <Col xs={12} md={3}>
                            <label
                              className="fw-semibold mb-2 d-block"
                              style={{
                                color: '#6b7280',
                                fontSize: '13px',
                                fontFamily: 'Inter, system-ui, sans-serif'}}
                            >
                              Course
                            </label>
                            <Form.Control
                              type="text"
                              list="announcement-course-options"
                              value={courseSearch}
                              onChange={(e) => handleCourseSearchChange(e.target.value)}
                              placeholder="Search course"
                              className="ann-filter-select"
                              onFocus={(e) => {
                                e.target.style.borderColor = '#223843'
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#D1D3D4'
                              }}
                            />
                            <datalist id="announcement-course-options">
                              {coursesList.map((c) => {
                                const cid = getCourseId(c)
                                return <option key={cid} value={getCourseLabel(c)} />
                              })}
                            </datalist>
                          </Col>
                          <Col xs={12} md={3}>
                            <label
                              className="fw-semibold mb-2 d-block"
                              style={{
                                color: '#6b7280',
                                fontSize: '13px',
                                fontFamily: 'Inter, system-ui, sans-serif'}}
                            >
                              Scope
                            </label>
                            <Form.Select
                              name="scope"
                              value={filters.scope}
                              onChange={handleFilterChange}
                              className="ann-filter-select"
                              onFocus={(e) => {
                                e.target.style.borderColor = '#223843'
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#D1D3D4'
                              }}
                            >
                              <option value="">Any</option>
                              <option value="school">School</option>
                              <option value="class">Class</option>
                              <option value="course">Course</option>
                            </Form.Select>
                          </Col>
                          <Col xs={12} md={3}>
                            <label
                              className="fw-semibold mb-2 d-block"
                              style={{
                                color: '#6b7280',
                                fontSize: '13px',
                                fontFamily: 'Inter, system-ui, sans-serif'}}
                            >
                              Status
                            </label>
                            <Form.Select
                              name="status"
                              value={filters.status}
                              onChange={handleFilterChange}
                              className="ann-filter-select"
                              onFocus={(e) => {
                                e.target.style.borderColor = '#223843'
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#D1D3D4'
                              }}
                            >
                              <option value="">Any</option>
                              <option value="draft">Draft</option>
                              <option value="published">Published</option>
                              <option value="archived">Archived</option>
                            </Form.Select>
                          </Col>
                        </Row>
                        <div className="d-flex justify-content-end mt-3">
                          <Button
                            type="button"
                            onClick={applyFilters}
                            style={{
                              backgroundColor: '#223843',
                              border: 'none',
                              borderRadius: '8px',
                              minWidth: '100px',
                              fontFamily: 'Inter, system-ui, sans-serif'}}
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

          <Card
            className="table-card"
            style={{
              border: '1px solid #D1D3D4',
              borderRadius: '12px',
              overflow: 'visible',
              backgroundColor: '#ffffff',
              position: 'relative'}}
          >
            <div
              className="table-wrapper"
              style={{
                overflowX: 'auto',
                overflowY: 'visible',
                position: 'relative',
                borderRadius: '12px'}}
            >
              {loading ? (
                <div className="d-flex justify-content-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : rows.length === 0 ? (
                <div className="text-center text-muted py-5 px-3" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                  No announcements yet. Create one to get started.
                </div>
              ) : (
                <Table
                  className="table mb-0"
                  style={{
                    borderRadius: '12px',
                    overflow: 'visible',
                    position: 'relative',
                    marginBottom: '0'}}
                >
                  <Thead>
                    <Tr style={{ backgroundColor: '#F3F4F6' }}>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif'}}
                      >
                        Title
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif'}}
                      >
                        Message
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif'}}
                      >
                        Scope
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif'}}
                      >
                        Status
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif'}}
                      >
                        Priority
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif'}}
                      >
                        Publish
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif'}}
                      >
                        Expire
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'center',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          whiteSpace: 'nowrap'}}
                      >
                        Action
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {displayRows.map((r, index) => {
                      const id = rowId(r)
                      const title = r.title || '—'
                      const msg = r.message ?? r.description ?? ''
                      const scope = r.scope ?? '—'
                      return (
                        <Tr
                          key={id ?? index}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                            transition: 'background-color 0.2s ease',
                            borderBottom: '1px solid #E5E7EB'}}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.06)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                          }}
                        >
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              fontWeight: 600,
                              minWidth: '160px'}}
                          >
                            {title}
                          </Td>
                          <Td
                            className="ann-message-cell"
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              maxWidth: '280px'}}
                          >
                            {truncate(msg)}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              textTransform: 'capitalize',
                              whiteSpace: 'nowrap'}}
                          >
                            {scope}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              whiteSpace: 'nowrap'}}
                          >
                            {statusBadge(r.status)}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              whiteSpace: 'nowrap'}}
                          >
                            {priorityBadge(r.priority)}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              whiteSpace: 'nowrap'}}
                          >
                            {formatDateTime(r.publish_at)}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              whiteSpace: 'nowrap'}}
                          >
                            {formatDateTime(r.expire_at)}
                          </Td>
                          <Td
                            className="text-center"
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              fontFamily: 'Inter, system-ui, sans-serif'}}
                          >
                            {/* Desktop — three-dots menu (match AddStudent.js) */}
                            <div className="d-none d-md-block">
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="light"
                                  id={`ann-actions-${id}`}
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    backgroundColor: 'transparent',
                                    border: '1px solid #D1D3D4',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'}}
                                  className="dropdown-toggle-no-caret"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#223843'
                                    e.currentTarget.style.borderColor = '#223843'
                                    e.currentTarget.style.color = '#ffffff'
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent'
                                    e.currentTarget.style.borderColor = '#D1D3D4'
                                    e.currentTarget.style.color = '#111827'
                                  }}
                                >
                                  <BsThreeDotsVertical style={{ color: 'inherit' }} />
                                </Dropdown.Toggle>

                                <Dropdown.Menu
                                  style={{
                                    borderRadius: '8px',
                                    border: '1px solid #D1D3D4',
                                    padding: '8px',
                                    minWidth: '180px'}}
                                >
                                  <Dropdown.Item
                                    onClick={() => navigate(`/announcement/view/${id}`)}
                                    style={{
                                      fontSize: '13px',
                                      padding: '10px 12px',
                                      color: '#111827',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      transition: 'all 0.2s ease',
                                      borderRadius: '6px',
                                      fontFamily: 'Inter, system-ui, sans-serif'}}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#223843'
                                      e.currentTarget.style.color = '#ffffff'
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent'
                                      e.currentTarget.style.color = '#111827'
                                    }}
                                  >
                                    <BsEyeFill size={18} /> View
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => navigate(`/announcement/edit/${id}`)}
                                    style={{
                                      fontSize: '13px',
                                      padding: '10px 12px',
                                      color: '#111827',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      transition: 'all 0.2s ease',
                                      borderRadius: '6px',
                                      fontFamily: 'Inter, system-ui, sans-serif'}}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#10B981'
                                      e.currentTarget.style.color = '#ffffff'
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent'
                                      e.currentTarget.style.color = '#111827'
                                    }}
                                  >
                                    <BsPencilFill size={18} /> Edit
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => setDeleteTarget({ id, title })}
                                    style={{
                                      fontSize: '13px',
                                      padding: '10px 12px',
                                      color: '#111827',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      transition: 'all 0.2s ease',
                                      borderRadius: '6px',
                                      fontFamily: 'Inter, system-ui, sans-serif'}}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#dc2626'
                                      e.currentTarget.style.color = '#ffffff'
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent'
                                      e.currentTarget.style.color = '#111827'
                                    }}
                                  >
                                    <BsTrashFill size={18} /> Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>

                            {/* Mobile — circular buttons (match AddStudent.js) */}
                            <div className="d-md-none d-flex justify-content-start gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => navigate(`/announcement/view/${id}`)}
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  backgroundColor: '#223843',
                                  border: 'none',
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease',
                                  cursor: 'pointer'}}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#223843'
                                  e.currentTarget.style.transform = 'scale(1.1)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#223843'
                                  e.currentTarget.style.transform = 'scale(1)'
                                }}
                                title="View"
                              >
                                <BsEyeFill size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={() => navigate(`/announcement/edit/${id}`)}
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  backgroundColor: '#10B981',
                                  border: 'none',
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease',
                                  cursor: 'pointer'}}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#059669'
                                  e.currentTarget.style.transform = 'scale(1.1)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#10B981'
                                  e.currentTarget.style.transform = 'scale(1)'
                                }}
                                title="Edit"
                              >
                                <BsPencilFill size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteTarget({ id, title })}
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  backgroundColor: '#dc2626',
                                  border: 'none',
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease',
                                  cursor: 'pointer'}}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#b91c1c'
                                  e.currentTarget.style.transform = 'scale(1.1)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#dc2626'
                                  e.currentTarget.style.transform = 'scale(1)'
                                }}
                                title="Delete"
                              >
                                <BsTrashFill size={16} />
                              </button>
                            </div>
                          </Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              )}
            </div>
          </Card>

          {!loading && rows.length > 0 && (
            <TablePagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              indexOfFirstItem={indexOfFirstItem}
              indexOfLastItem={indexOfLastItem}
              totalItems={rows.length}
              itemLabel="announcements"
              className="d-flex justify-content-between align-items-center gap-3 mb-3 mt-4 flex-wrap"
            />
          )}
        </Container>
      </div>

      <Modal show={Boolean(deleteTarget)} onHide={() => !deleting && setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete announcement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget
            ? `Delete “${deleteTarget.title}”? This cannot be undone.`
            : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={deleting} onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" disabled={deleting} onClick={confirmDelete}>
            {deleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting…
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
